<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    markRaw,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { useRouter } from 'vue-router/composables';
  import { useMenuV2 } from '@/composition/use-menu-v2';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { provideMenuImport } from '@/composition/menu-import/menu-import-context';
  import type {
    IMenuImportContext,
    TMenuImportEntry,
    TMenuPublishOutcome,
  } from '@/composition/menu-import/menu-import-context';
  import { useDishPhotos } from '@/composition/menu-import/use-dish-photos';
  import { useMenuCapture } from '@/composition/menu-import/use-menu-capture';
  import { useMenuImportSession } from '@/composition/menu-import/use-menu-import-session';
  import { useMenuImportTurns } from '@/composition/menu-import/use-menu-import-turns';
  import { EMPTY_FLOW_BAR } from '@/composition/store-setup/setup-flow-screen';
  import type {
    IFlowBarState,
    IFlowScreenHandle,
  } from '@/composition/store-setup/setup-flow-screen';
  import { useGtag } from '@/composition/use-gtag';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import {
    isUpdateDraft,
    liveHasChanges,
  } from '@/domain/menu-import/live-update';
  import { draftHasItems, draftSummary } from '@/domain/menu-import/menu-draft';
  import {
    PUBLISH_DIALOG_MODAL,
    publishConversions,
    publishDialogModel,
    publishedEvent,
    publishSource,
    publishUpdateOf,
    restaurantAfterPublish,
  } from '@/domain/menu-import/publish-dialog';
  import type {
    TPublishAttempt,
    TPublishDialogResult,
    TPublishSource,
  } from '@/domain/menu-import/publish-dialog';
  import {
    menuLaterTarget,
    publishPath,
  } from '@/domain/menu-import/workspace-view';
  import { fireAdsConversion } from '@/domain/store-setup/store-setup-analytics';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IPublishResponse } from '@/interfaces/IMenuImport';
  import type { IRestaurant } from '@/interfaces/IRestaurant';
  import { nextDomId } from '@/utils/dom-id.utils';
  import { ignoreNavigationFailure } from '@/utils/navigation.utils';
  import { vueData } from '@/VueData';
  import V2MenuSourceStep from './source/V2MenuSourceStep.vue';
  import V2MenuPublishDialog from './workspace/V2MenuPublishDialog.vue';
  import V2MenuWorkspace from './workspace/V2MenuWorkspace.vue';

  /**
   * The menu step of `/:id/setup` and the whole of `/:id/menu/import` (plan
   * §4.3 to §4.7, §11.2): builds the import session, the chat and the capture
   * tray, provides them to its screens and switches between the source
   * screen and the workspace (`session.view`). The page binds its single
   * bottom bar to this component's screen handle, which forwards to the inner
   * screen shown.
   */
  const props = defineProps<{
    entry: TMenuImportEntry;
    restaurant: IRestaurant;
    wide: boolean;
    phone: boolean;
  }>();
  const emit = defineEmits<{
    /** Published by this flow; `sessionId` is the session published (the update's undo). */
    (e: 'published', response: IPublishResponse, sessionId: string): void;
    /** Another device or tab published this session: the page moves on, without reporting a publish. */
    (e: 'published-elsewhere'): void;
    (e: 'skipped'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const gtag = useGtag();
  const router = useRouter();
  const modals = useModal();
  const ui = useUi();
  let alive = true;

  const session = useMenuImportSession({
    restaurant: () => props.restaurant,
    entry: props.entry,
  });
  const chat = useMenuImportTurns(session);
  const capture = useMenuCapture(session);
  const photos = useDishPhotos(session);
  /**
   * Raw: the page renders the workspace's top-bar actions outside the flow
   * (through V2MenuContextBridge), and a reactive copy travelling as a prop
   * would unwrap its refs.
   */
  const context: IMenuImportContext = markRaw({
    ...session,
    chat,
    capture,
    photos,
  });
  provideMenuImport(context);
  /** New for every mounted flow: the key of the page's context bridge. */
  const contextId = nextDomId('menu-flow');

  /** The inner screen of the `v-if` chain below (its exposed handle). */
  const activeView = ref<IFlowScreenHandle | null>(null);

  const loadState = session.loadState;
  const view = session.view;
  const ready = computed(() => loadState.value === 'ready');
  /** Pages added now join the dishes already in the draft. */
  const again = computed(() => draftHasItems(session.draft.value));
  /** The workspace sent the owner to type the menu («Escribirlo o pegarlo» after a failed read). */
  const pasteRequested = ref(false);
  /** The workspace fills the body edge to edge (full-bleed panes). */
  const bleed = computed(() => ready.value && view.value === 'workspace');

  //#region Screen handle
  /**
   * The inner screen's bar, busy while «Publicar» is handled (the
   * comparison, «Terminar», a publish the dialog left on its way); not
   * behind the open dialog, which shows its own.
   */
  const bar = computed<IFlowBarState>(() => {
    if (!ready.value || !activeView.value) return EMPTY_FLOW_BAR;
    const inner = activeView.value.bar;
    return publishing.value &&
      !publishDialogOpen.value &&
      inner.primaryLabel !== null
      ? { ...inner, primaryBusy: true }
      : inner;
  });
  const busy = computed(() => session.busy.value);
  const innerKey = computed(() => (ready.value ? view.value : loadState.value));

  function primary(): Promise<void> {
    return activeView.value?.primary() ?? Promise.resolve();
  }

  function secondary(): Promise<void> {
    return activeView.value?.secondary() ?? Promise.resolve();
  }

  function back(): Promise<void> {
    return activeView.value?.back() ?? Promise.resolve();
  }
  //#endregion

  function load() {
    void session.load();
  }

  /** From the workspace to the source screen (in again mode when the draft has dishes). */
  function openSource(paste: boolean) {
    pasteRequested.value = paste;
    session.view.value = 'source';
  }

  const { isMenuV2Active } = useMenuV2();

  /** «Seguir después»: the dashboard in the setup flow, the menu on /menu/import. */
  function later() {
    void router
      .push(
        menuLaterTarget(
          props.entry,
          props.restaurant.stringId,
          isMenuV2Active.value
        )
      )
      .catch(ignoreNavigationFailure);
  }

  //#region Publish (§4.7)
  interface IPublishFacts {
    source: TPublishSource;
    hiddenUnpriced: number;
  }

  const publishDialogOpen = ref(false);
  /** «Publicar menú» is being handled (the dialog, then its answer): that path reports and navigates. */
  const publishing = ref(false);
  /** The session whose publish this flow already reported: its own, or one made elsewhere. */
  let reportedSessionId: string | null = null;
  /**
   * Another device or tab published this session (a save answered
   * SESSION_PUBLISHED, or a refetch said so). Once nothing is being sent
   * (every write on a published session is refused), the page leaves the
   * workspace; nothing is reported (round 3 M2).
   */
  const publishedElsewhere = computed(
    () =>
      session.status.value === 'published' &&
      !publishing.value &&
      !session.busy.value
  );

  /**
   * Everything a successful publish reports (§4.7, §4.13, D28): GA, the Ads
   * conversions of a first publish, the store's new currency and languages
   * (App.vue then refreshes the currency), and the page's next screen. Runs
   * even when this flow is already gone: the publish happened. A replayed
   * answer (another device or tab published first, or a retry) reports
   * nothing: the first answer did.
   */
  function onPublished(
    response: IPublishResponse,
    facts: IPublishFacts,
    sessionId: string
  ) {
    reportedSessionId = sessionId;
    if (response.replayed !== true)
      gtag.event(
        'menu_published',
        publishedEvent(response, { ...facts, entry: props.entry })
      );
    for (const conversion of publishConversions(response))
      fireAdsConversion(conversion, vueData.conversion.bind(vueData));
    const current = vueData.restaurant;
    if (current && current.id === props.restaurant.id)
      vueData.setCurrentRestaurant(restaurantAfterPublish(current, response));
    emit('published', response, sessionId);
  }

  /** UNRESOLVED: this device was behind. Once caught up, the first red dish opens (else the first blocking question). */
  async function showUnresolved(itemIds: string[]) {
    await session.refresh();
    if (!alive) return;
    const itemId = itemIds.find((id) => !!session.draft.value?.items[id]);
    if (itemId !== undefined) {
      session.openItemId.value = itemId;
      return;
    }
    const screen = activeView.value;
    if (screen && screen.bar.primaryAriaDisabled) void screen.primary();
  }

  /** «Revisarlos»: the list, filtered to the dishes to check. */
  function reviewUnchecked() {
    session.openItemId.value = null;
    session.mode.value = 'list';
    session.filter.value = 'toCheck';
  }

  /**
   * What a publish that did not happen asks for: the menu or the draft
   * changed (a toast; the session reloaded it), red dishes this device did
   * not know about, or an error nobody saw inline (`toastErrors`).
   */
  async function afterUnpublished(
    outcome: TMenuPublishOutcome,
    toastErrors: boolean
  ) {
    if (outcome.result === 'changed') {
      ui.showToast(
        AlertType.Info,
        String(proxy.$t('menuImport.publish.changed'))
      );
    } else if (outcome.result === 'unresolved') {
      await showUnresolved(outcome.itemIds);
    } else if (
      toastErrors &&
      (outcome.result === 'nothingVisible' || outcome.result === 'failed')
    ) {
      ui.showToast(
        AlertType.Error,
        String(
          proxy.$t(
            outcome.result === 'nothingVisible'
              ? 'menuImport.publish.nothingVisible'
              : 'menuImport.publish.error'
          )
        )
      );
    }
  }

  /**
   * «Publicar menú» / «Publicar cambios» with nothing blocking it. An update
   * (or a store with dishes) first compares the draft with the menu as it
   * is now (§3.6); then «Terminar» (nothing changes, nothing to check)
   * publishes at once, anything else opens the dialog and does what its
   * answer asks for.
   */
  async function openPublish() {
    const sessionId = session.sessionId.value;
    if (publishDialogOpen.value || !session.draft.value || sessionId === null) return;
    // Behind a save that is not going out (offline, failed) the comparison
    // would wait: the dialog shows that save instead (the publish itself
    // still answers LIVE_CHANGED when the menu changed).
    const saved = session.saveState.value === 'saved';
    if (
      saved &&
      (isUpdateDraft(session.draft.value) || session.existingItems.value > 0)
    ) {
      const compared = await session.refreshLive();
      if (!alive || !compared) return;
    }
    const draft = session.draft.value;
    if (!draft) return;
    const screen = activeView.value;
    const path = publishPath({
      bar: screen ? screen.bar : null,
      update: isUpdateDraft(draft),
      hasChanges: liveHasChanges(draft),
      toCheck: draftSummary(draft).toCheck,
      saved: session.saveState.value === 'saved',
    });
    if (path === 'stop') return;
    // The comparison found something to fix first: it opens, as the bar would.
    if (path === 'blocked') {
      if (screen) void screen.primary();
      return;
    }
    const source = publishSource(
      session.snapshotMeta.value?.kind ?? 'read',
      draft
    );
    if (path === 'finish') {
      // «Terminar»: nothing to change and nothing to check, no dialog.
      const outcome = await session.publish();
      if (outcome.result === 'published') {
        onPublished(outcome.response, { source, hiddenUnpriced: 0 }, sessionId);
        return;
      }
      if (alive) await afterUnpublished(outcome, true);
      return;
    }
    publishDialogOpen.value = true;
    const model = publishDialogModel({
      draft,
      existingItems: session.existingItems.value,
      slug: props.restaurant.stringId,
      storeLanguageId: props.restaurant.languageId ?? null,
      storeCurrency: props.restaurant.currency,
      showDisabledItems: vueData.restaurantSettings?.showDisabledItems === true,
      locale: proxy.$i18n.locale,
      update: publishUpdateOf(draft),
    });
    const facts: IPublishFacts = {
      source,
      hiddenUnpriced: model.hiddenUnpriced,
    };
    /** The latest press of «Publicar» (a failed one can be pressed again). */
    const latest: {
      attempt: Promise<TMenuPublishOutcome> | null;
      settled: boolean;
    } = { attempt: null, settled: false };
    const publish = async (): Promise<TPublishAttempt> => {
      const pending = session.publish();
      latest.attempt = pending;
      latest.settled = false;
      const outcome = await pending;
      if (latest.attempt === pending) latest.settled = true;
      return outcome.result;
    };

    let result: TPublishDialogResult | null = null;
    try {
      result = await modals.showDynamicDialog<TPublishDialogResult | null>(
        V2MenuPublishDialog,
        {
          model,
          publish,
          // «Publicar» waits for the saves (and offers «Reintentar»).
          saveState: () => session.saveState.value,
          retrySaves: () => session.retrySaves(),
        },
        PUBLISH_DIALOG_MODAL,
        // Not closed by a tap beside it; «Seguir revisando» always closes it.
        { width: 520, clickToClose: false }
      );
    } finally {
      // A publish moves the page on, which may have unmounted this flow already.
      if (alive) publishDialogOpen.value = false;
    }
    /** Closed while its publish was still on its way: nobody saw how it ended. */
    const closedEarly = latest.attempt !== null && !latest.settled;
    // The dialog may be gone before its publish answered: the publish still counts.
    const outcome = latest.attempt ? await latest.attempt : null;
    if (outcome && outcome.result === 'published') {
      onPublished(outcome.response, facts, sessionId);
      return;
    }
    if (!alive) return;
    if (result === 'review') {
      reviewUnchecked();
      return;
    }
    // The dialog shows failures inline; closed before the answer, a toast says it.
    if (outcome) await afterUnpublished(outcome, closedEarly);
  }

  async function onPublishRequested() {
    if (publishing.value) return;
    publishing.value = true;
    try {
      await openPublish();
    } finally {
      // A publish moves the page on, which may have unmounted this flow already.
      if (alive) publishing.value = false;
    }
  }

  watch(publishedElsewhere, (published) => {
    const sessionId = session.sessionId.value;
    if (!published || sessionId === null || sessionId === reportedSessionId)
      return;
    reportedSessionId = sessionId;
    emit('published-elsewhere');
  });
  //#endregion

  watch(view, (current) => {
    if (current === 'workspace') pasteRequested.value = false;
  });

  // Every screen of the step, once it is known (§4.13).
  watch(
    [ready, view],
    ([isReady, current]) => {
      if (!isReady) return;
      gtag.event('setup_step_viewed', {
        step: current === 'source' ? 'menu_source' : 'menu_workspace',
        entry: props.entry,
      });
    },
    { immediate: true }
  );

  onMounted(load);

  onBeforeUnmount(() => {
    alive = false;
  });

  defineExpose({
    bar,
    busy,
    innerKey,
    primary,
    secondary,
    back,
    context,
    contextId,
  });
</script>

<template>
  <div class="v2-menu-flow" :class="{ 'is-bleed': bleed, 'is-wide': wide }">
    <div
      v-if="loadState === 'idle' || loadState === 'loading'"
      class="flow-loading"
      aria-busy="true"
    >
      <PosUiSkeleton class="loading-title" />
      <PosUiSkeleton class="loading-line" />
      <PosUiSkeleton class="loading-block" />
    </div>
    <PosUiEmptyState
      v-else-if="loadState === 'error'"
      class="flow-error"
      icon="alert-circle-outline"
      :title="$t('menuImport.errors.generic')"
    >
      <template #action>
        <PosUiButton color="neutral" variant="outline" @click="load">{{
          $t('storeSetup.location.retry')
        }}</PosUiButton>
      </template>
    </PosUiEmptyState>
    <V2MenuSourceStep
      v-else-if="view === 'source'"
      ref="activeView"
      :entry="entry"
      :again="again"
      :phone="phone"
      :restaurant-id="restaurant.id"
      :initial-paste="pasteRequested"
      @skip="emit('skipped')"
    />
    <V2MenuWorkspace
      v-else
      ref="activeView"
      :entry="entry"
      :wide="wide"
      :phone="phone"
      :country="restaurant.country"
      @source="openSource"
      @later="later"
      @publish="onPublishRequested"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-flow {
    @apply tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col;
    .flow-loading {
      @apply tw-mx-auto tw-flex tw-w-full tw-max-w-[54rem] tw-flex-col tw-gap-3;
      .loading-title {
        @apply tw-h-8 tw-w-2/3;
      }
      .loading-line {
        @apply tw-h-5 tw-w-full;
      }
      .loading-block {
        @apply tw-h-40 tw-w-full;
      }
    }
    .flow-error {
      @apply tw-flex-1;
    }
    // The workspace archetype: cancels the shell body's padding (V2SetupShell:
    // px-4, md:px-6; py-4, py-6 on the wide tier) so its panes reach the edges.
    &.is-bleed {
      @apply -tw-mx-4 -tw-my-4 tw-w-auto md:-tw-mx-6;
      &.is-wide {
        @apply -tw-my-6;
      }
    }
  }

  // Offline, the shell's banner stays above the workspace.
  .offline-banner + .v2-menu-flow.is-bleed {
    @apply tw-mt-0;
  }
</style>
